/**
 * Moderator review mode (Backlog 058): the pure parts.
 *
 * Kept free of React and of the stores so the rules a moderator relies on (who may switch it on,
 * what a pin's badge says, how a viewport becomes a query key) can be pinned by plain tests.
 */

/** Why an adventure is on the review layer. The server's `reviewReasons` values, verbatim. */
export type ReviewReason = 'pending' | 'moderatorReview' | 'validation' | 'report'

/** One review pin, exactly as `GET /api/adventures/map/review` returns it. */
export interface ReviewPinDto {
  id: string
  title: string
  latitude: number
  longitude: number
  /** The coordinate the map's marker layers read. Always sent; see the Discover marker contract. */
  location: { latitude: number; longitude: number }
  categoryId: number
  status: number
  moderationStatus: number
  /** Unknown strings are possible from a newer server; they are ignored, never shown raw. */
  reviewReasons: string[]
  openValidationCount: number
  openReportCount: number
}

export interface ReviewBounds {
  west: number
  south: number
  east: number
  north: number
}

/** Global roles that may use review mode. Mirrors the server's `IsModerator()`. */
const REVIEW_ROLES = ['Moderator', 'Admin']

export function canUseReviewMode(roles: readonly string[] | null | undefined): boolean {
  return !!roles && roles.some((role) => REVIEW_ROLES.includes(role))
}

/**
 * The badge text for a pin, one entry per reason, in the server's reason order.
 *
 * "N tickets" uses the open validation count; a `validation` reason with a zero count (a
 * server that has not filled the count in) still says something true rather than "0 tickets".
 */
export function reviewReasonLabels(
  pin: Pick<ReviewPinDto, 'reviewReasons' | 'openValidationCount'>
): string[] {
  const labels: string[] = []
  const seen = new Set<string>()
  for (const reason of pin.reviewReasons ?? []) {
    if (seen.has(reason)) continue
    seen.add(reason)
    switch (reason as ReviewReason) {
      case 'pending':
        labels.push('Pending')
        break
      case 'moderatorReview':
        labels.push('In review')
        break
      case 'validation':
        labels.push(pin.openValidationCount > 0 ? `${pin.openValidationCount} ${pin.openValidationCount === 1 ? 'ticket' : 'tickets'}` : 'Tickets')
        break
      case 'report':
        labels.push('Reported')
        break
      default:
        break
    }
  }
  return labels
}

/** One line on the pin: the first reason, and how many more the popup holds. */
export function reviewBadgeText(labels: string[]): string {
  if (labels.length === 0) return 'Review'
  return labels.length === 1 ? labels[0] : `${labels[0]} +${labels.length - 1}`
}

/** Decimal places review bounds are snapped to: 0.01 degrees, about 1.1 km. */
export const REVIEW_BOUNDS_DECIMALS = 2

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n))

/**
 * The viewport, snapped OUTWARD to a coarse grid before it becomes a query key.
 *
 * Raw viewport floats jitter on every settle of the map, and a key built from them refetches and
 * rebuilds the markers (and any open popup) on each one: the Discover popup strobe. Snapping
 * outward keeps the fetched box a superset of what is on screen, so nothing visible is missed.
 */
export function roundReviewBounds(bounds: ReviewBounds, decimals = REVIEW_BOUNDS_DECIMALS): ReviewBounds {
  const factor = 10 ** decimals
  // toFixed after the floor/ceil removes binary noise (0.1 + 0.2 style tails) from the key.
  const down = (n: number) => Number((Math.floor(n * factor) / factor).toFixed(decimals))
  const up = (n: number) => Number((Math.ceil(n * factor) / factor).toFixed(decimals))
  return {
    west: clamp(down(bounds.west), -180, 180),
    south: clamp(down(bounds.south), -90, 90),
    east: clamp(up(bounds.east), -180, 180),
    north: clamp(up(bounds.north), -90, 90),
  }
}

/** A review pin with a usable coordinate, read from `location` first like the marker layer does. */
export function reviewPinPosition(pin: ReviewPinDto): [number, number] | null {
  const lat = pin.location?.latitude ?? pin.latitude
  const lng = pin.location?.longitude ?? pin.longitude
  if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null
  }
  return [lat, lng]
}
